// Implements: F-SHR-003, F-UIX-007
/**
 * The new link, read-only, with its copy action. Pure presentation.
 *
 * The copy goes through the Clipboard API inside a `try/catch` (checklist 15):
 * a browser that has no API, refuses the permission or is not on a secure
 * origin gets the link SELECTED in its field and a notice to copy it by hand,
 * so the person can always take it with them. The confirmation is temporary,
 * and its timer is injected so no test sleeps (checklist 16).
 */

import { useEffect, useId, useRef, useState } from "react";
import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { Button } from "../../../shared/ui/atoms/Button";
import styles from "./ShareDialog.module.css";

/** Runs `callback` later and returns what cancels it. Injected so no test sleeps. */
export type Schedule = (callback: () => void, delayMs: number) => () => void;

const NOTICE_MS = 2000;

const defaultSchedule: Schedule = (callback, delayMs) => {
  const handle = setTimeout(callback, delayMs);
  return () => {
    clearTimeout(handle);
  };
};

export interface CopyLinkButtonProps {
  /** The absolute link; it lives in the dialog's state only, never in a store. */
  readonly url: string;
  readonly schedule?: Schedule | undefined;
}

type Notice = "copied" | "manual" | null;

export function CopyLinkButton({ url, schedule = defaultSchedule }: CopyLinkButtonProps): ReactElement {
  const { t } = useTranslation();
  const id = useId();
  const fieldRef = useRef<HTMLInputElement>(null);
  const [notice, setNotice] = useState<Notice>(null);
  const cancel = useRef<(() => void) | null>(null);

  useEffect(() => {
    // The field has just been revealed in place of the form: the focus goes to what replaced it.
    fieldRef.current?.focus();
    return () => {
      cancel.current?.();
    };
  }, []);

  const copy = async (): Promise<void> => {
    let next: Notice = "copied";
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // No API, no permission or no secure origin: the text is handed over by selection.
      next = "manual";
      fieldRef.current?.focus();
      fieldRef.current?.select();
    }
    cancel.current?.();
    setNotice(next);
    if (next === "copied") {
      cancel.current = schedule(() => {
        cancel.current = null;
        setNotice(null);
      }, NOTICE_MS);
    }
  };

  return (
    <div className={styles.copy}>
      <label htmlFor={id} className={styles.label}>
        {t("sharing.created.field")}
      </label>
      <div className={styles.copyRow}>
        <input id={id} ref={fieldRef} className={styles.url} type="text" value={url} readOnly />
        <Button
          variant="primary"
          size="sm"
          onClick={() => {
            void copy();
          }}
        >
          {t("sharing.copy")}
        </Button>
      </div>
      <div role="status" className={styles.notice}>
        {notice === "copied" ? t("sharing.copied") : null}
        {notice === "manual" ? t("sharing.copyManual") : null}
      </div>
    </div>
  );
}
